import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatApiError = (detail) => {
    if (detail == null) return t('common.error');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    }
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await register(email, password, name, language);
      i18n.changeLanguage(language);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = formatApiError(err.response?.data?.detail) || err.message;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 bg-[#0A0A0A]">
      <Card className="w-full max-w-md bg-[#141414] border-white/10 rounded-sm">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-2xl text-white">{t('auth.register')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-400">{t('auth.name')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-sm focus:border-white/30"
                placeholder={t('auth.namePlaceholder') || "Name"}
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-sm focus:border-white/30"
                placeholder="email@example.com"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-sm focus:border-white/30"
                placeholder="••••••••"
                data-testid="register-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="language" className="text-zinc-400">{t('auth.language')}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm" data-testid="register-language">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  <SelectItem value="ja" className="text-zinc-300 hover:text-white"><img src="https://flagcdn.com/w20/jp.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> 日本語</SelectItem>
                  <SelectItem value="en" className="text-zinc-300 hover:text-white"><img src="https://flagcdn.com/w20/us.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> English</SelectItem>
                  <SelectItem value="pt" className="text-zinc-300 hover:text-white"><img src="https://flagcdn.com/w20/br.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> Português</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.registerButton')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-white hover:underline" data-testid="link-login">
                {t('auth.login')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
